/* Stage 5 — B14 Project State controls (right rail + four dialogs).

   Reuses the accepted admin shell.  Contextual actions only:
     Active   → Place on Hold, Close Project
     On Hold  → Resume Project, Close Project
     Closed   → Reopen Project

   Consequential actions are visually distinct from routine actions.
*/
import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;


export function ProjectStateRail({ projectId, onStateChanged }) {
    const [state, setState] = useState(null);
    const [err, setErr] = useState("");
    const [outcomes, setOutcomes] = useState([]);
    const [dialog, setDialog] = useState(null);   // "hold"|"resume"|"close"|"reopen"
    const refresh = useCallback(async () => {
        try {
            const r = await axios.get(
                `${API}/admin/projects/${projectId}/state`,
                { withCredentials: true });
            setState(r.data);
        } catch (e) { setErr(e?.response?.data?.error || e.message); }
    }, [projectId]);
    // §2 correction — every successful transition must invalidate the
    // ONE authoritative workspace projection (`/workspace`) so header,
    // lifecycle strip, accordion and rail all rerender from the same
    // source.  We refresh the rail's own `/state` in parallel because
    // it exposes hold/close metadata beyond `/workspace`.
    const afterTransition = useCallback(async () => {
        await refresh();
        if (typeof onStateChanged === "function") {
            try { await onStateChanged(); } catch { /* parent handles */ }
        }
    }, [refresh, onStateChanged]);
    const loadOutcomes = useCallback(async () => {
        try {
            const r = await axios.get(`${API}/admin/closure-outcomes`,
                { withCredentials: true });
            setOutcomes(r.data?.items || []);
        } catch { /* silent */ }
    }, []);
    useEffect(() => { refresh(); loadOutcomes(); }, [refresh, loadOutcomes]);
    if (err) return <div className="text-[13px] text-red-700"
                          data-testid="state-rail-err">{err}</div>;
    if (!state) return <div className="text-[12px] text-[#666]"
                              data-testid="state-rail-loading">Loading…</div>;

    const routine = [
        {label: "Send Communication", testid: "rail-routine-comm"},
        {label: "View Files",         testid: "rail-routine-files"},
        {label: "Programme",          testid: "rail-routine-programme"},
        {label: "Log Call",           testid: "rail-routine-call"},
        {label: "Add Internal Note",  testid: "rail-routine-note"},
        {label: "Set Follow-up",      testid: "rail-routine-followup"},
    ];

    const consequentialLabels = {
        place_on_hold: "Place on Hold",
        resume_project: "Resume Project",
        close_project: "Close Project",
        reopen_project: "Reopen Project",
    };

    const badgeTone = {
        Active: "bg-emerald-100 text-emerald-800",
        "On Hold": "bg-amber-100 text-amber-800",
        Closed: "bg-slate-200 text-slate-700",
    }[state.project_state] || "bg-slate-100 text-slate-600";

    return (
        <aside className="border border-[#E5E2DC] rounded p-3 flex flex-col gap-3"
                data-testid="project-state-rail">
            <div className="flex items-center justify-between">
                <h3 className="font-manrope text-[13px] font-semibold text-charcoal">
                    Actions
                </h3>
                <span className={`px-2 py-0.5 rounded text-[11px] font-medium ${badgeTone}`}
                       data-testid="rail-state-badge">
                    {state.project_state}
                </span>
            </div>

            {/* Closure metadata surface (§10) */}
            {state.project_state === "Closed" && state.closure_outcome_code && (
                <div className="text-[12px] text-[#333] bg-[#F5F2ED] rounded p-2"
                      data-testid="rail-closure-meta">
                    <div><strong>Closure Outcome:</strong> {
                        outcomes.find(o => o.code === state.closure_outcome_code)?.label
                            || state.closure_outcome_code
                    }</div>
                    <div><strong>Closed by:</strong> {state.closed_by || "—"}</div>
                    <div><strong>Closed at:</strong> {(state.closed_at || "").slice(0, 16).replace("T", " ")}</div>
                    {state.closure_note && (
                        <div className="mt-1"><em>{state.closure_note}</em></div>)}
                </div>)}

            {/* Consequential state actions — visually distinct */}
            <div className="flex flex-col gap-1" data-testid="rail-consequential-group">
                <span className="text-[10px] uppercase tracking-wide text-[#888]">
                    Project State
                </span>
                {(state.valid_actions || []).map((a) => {
                    const isReopen = a === "reopen_project";
                    const isClose = a === "close_project";
                    const isHold = a === "place_on_hold";
                    const tone = isReopen
                        ? "bg-emerald-700 hover:bg-emerald-800"
                        : isClose
                            ? "bg-red-700 hover:bg-red-800"
                            : isHold
                                ? "bg-amber-700 hover:bg-amber-800"
                                : "bg-[#A8543F] hover:bg-[#8B4535]";
                    return (
                        <button key={a} type="button"
                                onClick={() => setDialog(a === "place_on_hold" ? "hold"
                                    : a === "resume_project" ? "resume"
                                    : a === "close_project" ? "close"
                                    : "reopen")}
                                className={`${tone} text-white text-[12px] px-3 py-2 rounded text-left`}
                                data-testid={`rail-action-${a}`}>
                            {consequentialLabels[a]}
                        </button>);
                })}
            </div>

            {/* Routine actions — visually distinct */}
            <div className="flex flex-col gap-1 border-t border-[#E5E2DC] pt-2"
                  data-testid="rail-routine-group">
                <span className="text-[10px] uppercase tracking-wide text-[#888]">
                    Routine
                </span>
                {routine.map((r) => (
                    <button key={r.label} type="button"
                            className="border border-[#E5E2DC] hover:bg-[#F5F2ED] text-[12px] px-3 py-1.5 rounded text-left"
                            data-testid={r.testid}>
                        {r.label}
                    </button>))}
            </div>

            {dialog === "hold" && (
                <HoldDialog projectId={projectId} state={state}
                             onClose={() => setDialog(null)}
                             onDone={() => { setDialog(null); afterTransition(); }} />)}
            {dialog === "resume" && (
                <ResumeDialog projectId={projectId} state={state}
                                onClose={() => setDialog(null)}
                                onDone={() => { setDialog(null); afterTransition(); }} />)}
            {dialog === "close" && (
                <CloseDialog projectId={projectId} state={state}
                              outcomes={outcomes}
                              onClose={() => setDialog(null)}
                              onDone={() => { setDialog(null); afterTransition(); }} />)}
            {dialog === "reopen" && (
                <ReopenDialog projectId={projectId} state={state}
                               onClose={() => setDialog(null)}
                               onDone={() => { setDialog(null); afterTransition(); }} />)}
        </aside>);
}


// ----------------------------------------------------------------------
// Dialogs
// ----------------------------------------------------------------------
function Dialog({ title, testid, children, onClose }) {
    return (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center"
              data-testid={testid || "dialog"}>
            <div className="bg-white rounded shadow-lg w-[min(560px,92vw)] max-h-[92vh] overflow-y-auto">
                <div className="flex items-center justify-between border-b border-[#E5E2DC] px-4 py-2">
                    <h2 className="font-manrope text-[15px] font-semibold">{title}</h2>
                    <button type="button" onClick={onClose}
                             className="text-[13px] text-[#666]"
                             data-testid={`${testid || "dialog"}-close`}>×</button>
                </div>
                <div className="p-4">{children}</div>
            </div>
        </div>);
}

export function HoldDialog({ projectId, state, onClose, onDone }) {
    const [reason, setReason] = useState("");
    const [note, setNote] = useState("");
    const [reviewAt, setReviewAt] = useState("");
    // Communications Safety — explicit opt-in for the workflow-triggered
    // client email. Defaults OFF each time the dialog opens.
    const [notifyClient, setNotifyClient] = useState(false);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const submit = async (e) => {
        e.preventDefault();
        setBusy(true); setErr("");
        try {
            await axios.post(
                `${API}/admin/projects/${projectId}/state/place-on-hold`,
                { expected_version: state.lifecycle_version,
                  hold_reason: reason, hold_note: note,
                  // Legacy business-audit flag on the hold record.
                  // Retained in the payload with a safe default so
                  // historical data model / backend behaviour is
                  // unchanged; UI checkbox removed per cleanup brief.
                  client_notification_required: false,
                  notify_client: notifyClient,
                  review_resume_at: reviewAt
                      ? new Date(reviewAt).toISOString() : null },
                { withCredentials: true });
            onDone();
        } catch (e2) { setErr(e2?.response?.data?.reason || e2?.response?.data?.error || e2.message); }
        finally { setBusy(false); }
    };
    return (
        <Dialog title="Place on Hold" testid="hold-dialog" onClose={onClose}>
            <form onSubmit={submit} className="flex flex-col gap-2">
                <p className="text-[12px] text-[#666]">
                    Current Workstage <strong>WS{state.current_workstage}</strong> is preserved.
                    Workstage history is not rewritten.
                </p>
                <label className="text-[12px]">Hold Reason
                    <input required value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            className="mt-1 w-full border border-[#E5E2DC] rounded px-2 py-1"
                            data-testid="hold-inp-reason" />
                </label>
                <label className="text-[12px]">Hold Note
                    <textarea required value={note} rows={3}
                                onChange={(e) => setNote(e.target.value)}
                                className="mt-1 w-full border border-[#E5E2DC] rounded px-2 py-1"
                                data-testid="hold-inp-note" />
                </label>
                <label className="text-[12px]">Review / Resume date (optional)
                    <input type="datetime-local" value={reviewAt}
                            onChange={(e) => setReviewAt(e.target.value)}
                            className="mt-1 w-full border border-[#E5E2DC] rounded px-2 py-1"
                            data-testid="hold-inp-review" />
                </label>
                <label className="text-[12px] flex items-center gap-2">
                    <input type="checkbox" checked={notifyClient}
                            onChange={(e) => setNotifyClient(e.target.checked)}
                            data-testid="hold-inp-notify-client" />
                    Send client notification
                </label>
                {err && <p className="text-[12px] text-red-700"
                              data-testid="hold-err">{err}</p>}
                <div className="flex justify-end gap-2 mt-2">
                    <button type="button" onClick={onClose}
                             className="text-[12px] px-3 py-1.5">Cancel</button>
                    <button type="submit" disabled={busy}
                             className="bg-amber-700 hover:bg-amber-800 text-white text-[12px] px-3 py-1.5 rounded"
                             data-testid="hold-submit">
                        {busy ? "Placing…" : "Place on Hold"}
                    </button>
                </div>
            </form>
        </Dialog>);
}

export function ResumeDialog({ projectId, state, onClose, onDone }) {
    const [note, setNote] = useState("");
    const [nextAction, setNextAction] = useState("");
    const [confirmed, setConfirmed] = useState(false);
    const [notifyClient, setNotifyClient] = useState(false);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const submit = async (e) => {
        e.preventDefault();
        setBusy(true); setErr("");
        try {
            await axios.post(
                `${API}/admin/projects/${projectId}/state/resume`,
                { expected_version: state.lifecycle_version,
                  resume_note: note,
                  next_action_confirmed: confirmed,
                  next_action_summary: nextAction,
                  notify_client: notifyClient },
                { withCredentials: true });
            onDone();
        } catch (e2) { setErr(e2?.response?.data?.reason || e2?.response?.data?.error || e2.message); }
        finally { setBusy(false); }
    };
    return (
        <Dialog title="Resume Project" testid="resume-dialog" onClose={onClose}>
            <form onSubmit={submit} className="flex flex-col gap-2">
                <p className="text-[12px] text-[#666]">
                    The same Workstage <strong>WS{state.current_workstage}</strong> will resume.
                    A new Workstage record is NOT created.
                </p>
                <label className="text-[12px]">Resume Note
                    <textarea required value={note} rows={3}
                                onChange={(e) => setNote(e.target.value)}
                                className="mt-1 w-full border border-[#E5E2DC] rounded px-2 py-1"
                                data-testid="resume-inp-note" />
                </label>
                <label className="text-[12px]">Confirmed Next Action
                    <input required value={nextAction}
                            onChange={(e) => setNextAction(e.target.value)}
                            className="mt-1 w-full border border-[#E5E2DC] rounded px-2 py-1"
                            data-testid="resume-inp-next-action" />
                </label>
                <label className="text-[12px] flex items-center gap-2">
                    <input type="checkbox" checked={confirmed}
                            onChange={(e) => setConfirmed(e.target.checked)}
                            data-testid="resume-inp-confirmed" />
                    I confirm the Next Action above is deliberate and correct.
                </label>
                <label className="text-[12px] flex items-center gap-2">
                    <input type="checkbox" checked={notifyClient}
                            onChange={(e) => setNotifyClient(e.target.checked)}
                            data-testid="resume-inp-notify-client" />
                    Send client notification
                </label>
                {err && <p className="text-[12px] text-red-700"
                              data-testid="resume-err">{err}</p>}
                <div className="flex justify-end gap-2 mt-2">
                    <button type="button" onClick={onClose}
                             className="text-[12px] px-3 py-1.5">Cancel</button>
                    <button type="submit" disabled={busy || !confirmed}
                             className="bg-emerald-700 hover:bg-emerald-800 text-white text-[12px] px-3 py-1.5 rounded"
                             data-testid="resume-submit">
                        {busy ? "Resuming…" : "Resume Project"}
                    </button>
                </div>
            </form>
        </Dialog>);
}

export function CloseDialog({ projectId, state, outcomes, onClose, onDone }) {
    const [outcome, setOutcome] = useState("");
    const [note, setNote] = useState("");
    const [notifyClient, setNotifyClient] = useState(false);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const [blockers, setBlockers] = useState([]);
    const active = (outcomes || []).filter((o) => o.active);
    const submit = async (e) => {
        e.preventDefault();
        setBusy(true); setErr(""); setBlockers([]);
        try {
            await axios.post(
                `${API}/admin/projects/${projectId}/state/close`,
                { expected_version: state.lifecycle_version,
                  closure_outcome_code: outcome,
                  closure_note: note,
                  notify_client: notifyClient },
                { withCredentials: true });
            onDone();
        } catch (e2) {
            const d = e2?.response?.data;
            if (d?.blockers) setBlockers(d.blockers);
            setErr(d?.reason || d?.error || e2.message);
        }
        finally { setBusy(false); }
    };
    return (
        <Dialog title="Close Project" testid="close-dialog" onClose={onClose}>
            <form onSubmit={submit} className="flex flex-col gap-2">
                <p className="text-[12px] text-[#666]">
                    Current Workstage <strong>WS{state.current_workstage}</strong>.
                    Early closure marks this Workstage as
                    <em> "Ended by Project Closure"</em> (not Completed).
                    Normal Post Completion path is handled by
                    <em> Complete Post Completion &amp; Close Project</em>.
                </p>
                <label className="text-[12px]">Closure Outcome
                    <select required value={outcome}
                             onChange={(e) => setOutcome(e.target.value)}
                             className="mt-1 w-full border border-[#E5E2DC] rounded px-2 py-1"
                             data-testid="close-inp-outcome">
                        <option value="">— select —</option>
                        {active.map((o) => (
                            <option key={o.code} value={o.code}>
                                {o.label}
                            </option>))}
                    </select>
                </label>
                <label className="text-[12px]">Closure Note (mandatory)
                    <textarea required value={note} rows={4}
                                onChange={(e) => setNote(e.target.value)}
                                className="mt-1 w-full border border-[#E5E2DC] rounded px-2 py-1"
                                data-testid="close-inp-note" />
                </label>
                <label className="text-[12px] flex items-center gap-2">
                    <input type="checkbox" checked={notifyClient}
                            onChange={(e) => setNotifyClient(e.target.checked)}
                            data-testid="close-inp-notify-client" />
                    Send client notification
                </label>
                {blockers.length > 0 && (
                    <div className="text-[12px] text-red-700 bg-red-50 rounded p-2"
                          data-testid="close-blockers">
                        <strong>Cannot close — resolve blockers in their owning workflows:</strong>
                        <ul className="list-disc ml-5 mt-1">
                            {blockers.map((b, i) => (
                                <li key={i}>{b.reason || b.kind}</li>))}
                        </ul>
                    </div>)}
                {err && !blockers.length && (
                    <p className="text-[12px] text-red-700"
                        data-testid="close-err">{err}</p>)}
                <div className="flex justify-end gap-2 mt-2">
                    <button type="button" onClick={onClose}
                             className="text-[12px] px-3 py-1.5">Cancel</button>
                    <button type="submit" disabled={busy}
                             className="bg-red-700 hover:bg-red-800 text-white text-[12px] px-3 py-1.5 rounded"
                             data-testid="close-submit">
                        {busy ? "Closing…" : "Close Project"}
                    </button>
                </div>
            </form>
        </Dialog>);
}

export function ReopenDialog({ projectId, state, onClose, onDone }) {
    const [target, setTarget] = useState(state.current_workstage || 1);
    const [reason, setReason] = useState("");
    const [note, setNote] = useState("");
    const [authority, setAuthority] = useState("admin/manual");
    const [notifyClient, setNotifyClient] = useState(false);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const submit = async (e) => {
        e.preventDefault();
        setBusy(true); setErr("");
        try {
            await axios.post(
                `${API}/admin/projects/${projectId}/state/reopen`,
                { expected_version: state.lifecycle_version,
                  target_workstage: Number(target),
                  reopen_reason: reason,
                  reopen_note: note,
                  authority_reference: authority,
                  notify_client: notifyClient },
                { withCredentials: true });
            onDone();
        } catch (e2) { setErr(e2?.response?.data?.reason || e2?.response?.data?.error || e2.message); }
        finally { setBusy(false); }
    };
    return (
        <Dialog title="Reopen Project" testid="reopen-dialog" onClose={onClose}>
            <form onSubmit={submit} className="flex flex-col gap-2">
                <p className="text-[12px] text-[#666]">
                    Reopening returns the project to <strong>Active</strong> at the explicit
                    Target Workstage below.  There is no Workstage 8.  Closed packages, contracts
                    and completed actions remain closed.
                </p>
                <label className="text-[12px]">Target Workstage (1–7)
                    <select required value={target}
                             onChange={(e) => setTarget(e.target.value)}
                             className="mt-1 w-full border border-[#E5E2DC] rounded px-2 py-1"
                             data-testid="reopen-inp-target">
                        {[1,2,3,4,5,6,7].map((w) =>
                            <option key={w} value={w}>WS{w}</option>)}
                    </select>
                </label>
                <label className="text-[12px]">Reopen Reason
                    <input required value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            className="mt-1 w-full border border-[#E5E2DC] rounded px-2 py-1"
                            data-testid="reopen-inp-reason" />
                </label>
                <label className="text-[12px]">Reopen Note
                    <textarea required value={note} rows={3}
                                onChange={(e) => setNote(e.target.value)}
                                className="mt-1 w-full border border-[#E5E2DC] rounded px-2 py-1"
                                data-testid="reopen-inp-note" />
                </label>
                <label className="text-[12px]">Authority reference
                    <input required value={authority}
                            onChange={(e) => setAuthority(e.target.value)}
                            className="mt-1 w-full border border-[#E5E2DC] rounded px-2 py-1"
                            data-testid="reopen-inp-authority" />
                </label>
                <label className="text-[12px] flex items-center gap-2">
                    <input type="checkbox" checked={notifyClient}
                            onChange={(e) => setNotifyClient(e.target.checked)}
                            data-testid="reopen-inp-notify-client" />
                    Send client notification
                </label>
                {err && <p className="text-[12px] text-red-700"
                              data-testid="reopen-err">{err}</p>}
                <div className="flex justify-end gap-2 mt-2">
                    <button type="button" onClick={onClose}
                             className="text-[12px] px-3 py-1.5">Cancel</button>
                    <button type="submit" disabled={busy}
                             className="bg-emerald-700 hover:bg-emerald-800 text-white text-[12px] px-3 py-1.5 rounded"
                             data-testid="reopen-submit">
                        {busy ? "Reopening…" : "Reopen Project"}
                    </button>
                </div>
            </form>
        </Dialog>);
}
